import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ImageModal from '../components/ImageModal';
import '../styles/AIDoctor.css';
import LoadingButton from '../components/LoadingButton';
import InteractiveEyes from '../components/InteractiveEyes';
import { Palette, ImagePlus, Sprout } from 'lucide-react';
import Button from '@mui/material/Button';

// Sample response data for testing Gemini API integration
const sampleGeminiResponse = {
  "status": "success",
  "data": {
    "analysis": {
      "plantIdentification": {
        "name": "Grapevine",
        "scientificName": "Vitis vinifera"
      },
      "healthAssessment": {
        "isHealthy": false,
        "confidence": 0.92
      },
      "diagnosis": {
        "condition": "Downy Mildew",
        "description": "Downy mildew is a fungal disease caused by Plasmopara viticola. The infection appears as yellow to white patches on the upper leaf surface and fuzzy white growth on the underside of leaves. It thrives in humid conditions and can spread rapidly throughout the vineyard, potentially causing significant crop loss if left untreated."
      },
      "treatment": {
        "recommendations": [
          "Remove and destroy infected leaves to reduce the source of infection",
          "Improve air circulation by proper pruning and spacing between plants",
          "Apply copper-based fungicides as a preventative measure",
          "Water at the base of plants to keep foliage dry",
          "Apply organic fungicides containing potassium bicarbonate for organic management"
        ]
      }
    },
    "image": "data:image/jpeg;base64,/9j/4AA..."
  }
};

// You can adjust this to match your backend URL
const API_BASE_URL = 'https://your-garden-guru-backend.vercel.app/api';
// const API_BASE_URL = 'http://localhost:5000/api';

const AIDoctor = () => {
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [location, setLocation] = useState({ latitude: null, longitude: null });
  const [usingCurrentLocation, setUsingCurrentLocation] = useState(false);
  const [useSampleData, setUseSampleData] = useState(false);

  // Modal state for enlarged images
  const [modalImage, setModalImage] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // New state to track if we already attempted location detection
  const [locationAttempted, setLocationAttempted] = useState(false);

  // State for expandable sections
  const [diagnosisExpanded, setDiagnosisExpanded] = useState(true);
  const [treatmentExpanded, setTreatmentExpanded] = useState(true);

  // Reference for the drag and drop area
  const dropAreaRef = useRef(null);
  const fileInputRef = useRef(null);

  const RATE_LIMIT_KEY = 'plant_doctor_last_request';
  const MIN_REQUEST_INTERVAL = 10000;

  // Function to open image modal
  const openImageModal = (imageUrl) => {
    setModalImage(imageUrl);
    setShowModal(true);
  };

  // Function to close image modal
  const closeImageModal = () => {
    setShowModal(false);
    setModalImage(null);
  };

  // Toggle expandable sections
  const toggleDiagnosis = () => {
    setDiagnosisExpanded(!diagnosisExpanded);
  };

  const toggleTreatment = () => {
    setTreatmentExpanded(!treatmentExpanded);
  };

  // Function to get user's current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      // Success callback
      (position) => {
        const { latitude, longitude } = position.coords;
        setLocation({ latitude, longitude });
        setUsingCurrentLocation(true);
        setLoading(false);
      },
      // Error callback
      (error) => {
        setLoading(false);
        console.error('Error getting location:', error);
        let errorMessage = 'Unable to get your location.';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access was denied. You can enable it manually.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'The request to get your location timed out.';
            break;
          default:
            errorMessage = 'An unknown error occurred getting your location.';
        }

        setError(errorMessage);
      },
      // Options
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );

    // Mark that we attempted to get location
    setLocationAttempted(true);
  };

  // Setup drag and drop event listeners
  useEffect(() => {
    const dropArea = dropAreaRef.current;

    if (dropArea) {
      const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dropArea.classList.add('ai-doctor-drop-area-dragging');
      };

      const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dropArea.classList.remove('ai-doctor-drop-area-dragging');
      };

      const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dropArea.classList.remove('ai-doctor-drop-area-dragging');

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
          // Check if the dropped file is an image
          if (!files[0].type.startsWith('image/')) {
            setError("Please drop a valid image file (JPEG, PNG, etc.)");
            return;
          }
          handleImageFile(files[0]);
        }
      };

      // Add event listeners
      dropArea.addEventListener('dragover', handleDragOver);
      dropArea.addEventListener('dragleave', handleDragLeave);
      dropArea.addEventListener('drop', handleDrop);

      // Clean up
      return () => {
        dropArea.removeEventListener('dragover', handleDragOver);
        dropArea.removeEventListener('dragleave', handleDragLeave);
        dropArea.removeEventListener('drop', handleDrop);
      };
    }
  }, []);

  // Automatically get location when component mounts
  useEffect(() => {
    if (!locationAttempted) {
      getCurrentLocation();
    }
  }, [locationAttempted]);

  // Process image file (used by both drag-drop and file input)
  const handleImageFile = (file) => {
    if (!file) {
      return;
    }

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      setError("Please select a valid image file (JPEG, PNG, etc.)");
      return;
    }

    // Check file size (optional, set to 10MB max)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > MAX_FILE_SIZE) {
      setError(`Image is too large. Please select an image under ${MAX_FILE_SIZE / (1024 * 1024)}MB.`);
      return;
    }

    setImage(file);
    setError(null); // Clear any previous errors

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.onerror = () => {
      setError("Error reading file. Please try another image.");
    };
    reader.readAsDataURL(file);
  };

  // Handle file selection from input
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageFile(file);
    }
  };

  // Handle click on drop area to trigger file input dialog
  const handleDropAreaClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Resize the image before converting to base64
  const resizeImage = (file) => {
    return new Promise((resolve, reject) => {
      // Validate file type again as a safeguard
      if (!file.type.startsWith('image/')) {
        reject(new Error("Invalid file type. Only images are supported."));
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          // Create a canvas and resize the image
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 800;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convert canvas to blob
          canvas.toBlob((blob) => {
            resolve(blob);
          }, file.type, 0.85); // 0.85 is the quality, adjust as needed
        };
        img.onerror = () => {
          reject(new Error("Failed to load image. The file may be corrupted or not a valid image."));
        };
        img.src = event.target.result;
      };
      reader.onerror = () => {
        reject(new Error("Error reading file"));
      };
    });
  };

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result;
        // Remove the prefix (e.g., "data:image/jpeg;base64,")
        const base64 = base64String.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Submit the image for analysis
  const handleSubmit = async (e) => {
    e.preventDefault();

    // If using sample data checkbox is checked, use the sample data
    if (useSampleData) {
      setLoading(true);
      // Simulate API delay
      setTimeout(() => {
        // Use the sample Gemini response
        setResult(sampleGeminiResponse);
        setLoading(false);
      }, 1500);
      return;
    }

    if (!image) {
      setError("Please select an image first");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const resizedImageBlob = await resizeImage(image);
      const base64Image = await fileToBase64(resizedImageBlob);

      const requestData = {
        images: [base64Image],
        similar_images: true
      };

      // Add location if available
      if (location.latitude && location.longitude) {
        requestData.latitude = location.latitude;
        requestData.longitude = location.longitude;
      }

      // Use the crop identification endpoint (it now uses Gemini internally)
      const endpoint = `${API_BASE_URL}/crop/identify`;

      console.log(`Sending request to: ${endpoint}`);

      const response = await axios.post(endpoint, requestData);
      console.log("API Response:", response.data);
      setResult(response.data);
    } catch (err) {
      console.error("Error analysing image:", err);
      setError(err.response?.data?.message || "An error occurred during analysis");
    } finally {
      setLoading(false);
    }
  };

  // Reset the form
  const handleReset = () => {
    setImage(null);
    setPreview(null);
    setResult(null);
    setError(null);
    // Keep the location data for subsequent scans
  };

  // Retry getting location if it failed initially or was denied
  const handleRetryLocation = () => {
    getCurrentLocation();
  };

  // Helper function to get analysis data safely
  const getAnalysisData = () => {
    if (!result || !result.data || !result.data.analysis) {
      return null;
    }
    return result.data.analysis;
  };

  // Get the analysis data
  const analysisData = getAnalysisData();

  // Helper function to check if a plant was detected
  const isPlantDetected = () => {
    if (!analysisData || !analysisData.plantIdentification) return false;
    
    const plantName = analysisData.plantIdentification.name;
    // Consider "Not a plant" as no plant detected
    return plantName && plantName !== "Not a plant" && plantName.toLowerCase() !== "no plant detected";
  };

  // Function to render treatment recommendations with checkmarks
  const renderTreatmentRecommendations = (recommendations) => {
    if (!recommendations || recommendations.length === 0) {
      return <p className="ai-doctor-no-treatments">No specific treatment recommendations available.</p>;
    }

    return (
      <ul className="ai-doctor-treatment-list">
        {recommendations.map((treatment, index) => (
          <li key={index} className="ai-doctor-treatment-item">
            <Sprout size={16} className="ai-doctor-treatment-icon" />
            <span className="ai-doctor-treatment-text">{treatment}</span>
          </li>
        ))}
      </ul>
    );
  };

  // Function to render an appropriate message when no plant is detected
  const renderNoPlantMessage = () => {
    return (
      <div className="ai-doctor-no-plant-message">
        <svg xmlns="http://www.w3.org/2000/svg" className="ai-doctor-no-plant-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="48" height="48">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <h3 className="ai-doctor-no-plant-title">No Plant Detected</h3>
        <p className="ai-doctor-no-plant-description">
          We couldn't identify any plant in the uploaded image. Please upload a clear image of a plant for diagnosis.
        </p>
      </div>
    );
  };

  return (
    <div className="ai-doctor-main-container">
      <div className="ai-doctor-container">
        {/* Page header */}
        {/* <div className="ai-doctor-header">
          <h1 className="ai-doctor-title">Plant's Doctor</h1>
          <p className="ai-doctor-subtitle">AI-powered plant diagnosis & treatment recommendations</p>
        </div> */}

        <div className="ai-doctor-grid">
          {/* Left Column - Canvas Upload Area */}
          <div className="ai-doctor-form-column">
            <div className="ai-doctor-card canvas-card">
              <div className="card-inner">
                <h2 className="ai-doctor-card-title"> 
                  {/* <Palette size={24} /> */}
                  Upload your plant image
                </h2>
                <div className="ai-doctor-card-content">
                  <div className="ai-doctor-form-group">
                    {/* Drag-drop area */}
                    <div
                      className="ai-doctor-drop-area"
                      ref={dropAreaRef}
                      onClick={handleDropAreaClick}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="ai-doctor-file-input"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                      />

                      {preview ? (
                        <img
                          src={preview}
                          alt="Preview"
                          className="ai-doctor-preview-image-in-drop-area"
                          onClick={(e) => {
                            e.stopPropagation();
                            openImageModal(preview);
                          }}
                        />
                      ) : (
                        <div className="ai-doctor-drop-content">
                          <div className="ai-doctor-drop-icon">
                            <ImagePlus size={48} />
                          </div>
                          <p className="ai-doctor-drop-text">Compose your Scene</p>
                          <p className="ai-doctor-drop-subtext">Upload Plant Photo (JPG, PNG)</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Only show location warning when no location is available */}
                  {!usingCurrentLocation && (
                    <div className="ai-doctor-form-group">
                      <div className="ai-doctor-location-status warning">
                        <p className="ai-doctor-location-status-text">
                          <span className="ai-doctor-location-icon">!</span> Location Not Available
                        </p>
                        <button
                          type="button"
                          onClick={handleRetryLocation}
                          disabled={loading}
                          className="ai-doctor-location-button"
                        >
                          Add Location
                        </button>
                        <p className="ai-doctor-location-help">
                          Adding your location helps improve diagnosis accuracy
                        </p>
                      </div>
                    </div>
                  )}

                  {error && (
                    <div className="ai-doctor-error">
                      {error}
                    </div>
                  )}

                  <div className="ai-doctor-button-group">
                    <Button
                      variant='outlined'
                      onClick={handleReset}
                      className="ai-doctor-reset-button"
                      sx={{
                        color: "#6b8e23",
                        borderColor: "#6b8e23",
                        "&:hover": {
                          color: "#f0ebe5",
                          backgroundColor: "#6b8e23" // optional
                        }
                      }}
                    >
                      Reset
                    </Button>

                    <Button
                      variant='contained'
                      onClick={handleSubmit}
                      disabled={(!image && !useSampleData) || loading}
                      className='ai-doctor-submit-button'
                      sx={{
                        backgroundColor: "#6b8e23",
                        // "&:hover": {
                        //   backgroundColor: "rgb(10, 180, 110)"
                        // }
                      }}
                    >
                      {/* <Brush size={20} className="ai-doctor-button-icon" /> */}
                      {loading ? "Analysing..." : "Diagnose My Plant"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Results */}
          <div className="ai-doctor-results-column">
            <div className="ai-doctor-card">
              <h2 className="ai-doctor-card-title">
                {/* <Feather size={24} /> */}
                Diagnosis Gallery
              </h2>

              <div className="ai-doctor-card-content">
                {loading ? (
                  <div className="ai-doctor-loading">
                    {/* Loading animation with LoadingButton */}
                    <div className="ai-doctor-custom-loading">
                      <LoadingButton />
                    </div>
                    <p className="ai-doctor-loading-text">
                      Analysing your plant...
                    </p>
                    <p className="ai-doctor-loading-subtext">
                      Our AI is examining your image
                    </p>
                  </div>
                ) : analysisData ? (
                  <div className="ai-doctor-results">
                    {isPlantDetected() ? (
                      <>
                        {/* Plant Identification */}
                        <div className="ai-doctor-identification-section">
                          <h2 className="ai-doctor-plant-name">{analysisData.plantIdentification.name}</h2>
                          {analysisData.plantIdentification.scientificName && (
                            <p className="ai-doctor-scientific-name">
                              {analysisData.plantIdentification.scientificName}
                            </p>
                          )}
                        </div>

                        {/* Health Assessment - Only show if a plant is detected */}
                        {analysisData.healthAssessment && (
                          <div className="ai-doctor-health-section">
                            <div className="ai-doctor-health-status">
                              <span className="ai-doctor-health-label">Health Status</span>
                              <div className="ai-doctor-health-indicator">
                                <div className="ai-doctor-health-status-text">
                                  <span
                                    className={`ai-doctor-health-icon ${analysisData.healthAssessment.isHealthy ? 'healthy' : 'unhealthy'}`}
                                  >
                                    {analysisData.healthAssessment.isHealthy ? '✓' : '⚠'}
                                  </span>
                                  <span
                                    className={`ai-doctor-health-text ${analysisData.healthAssessment.isHealthy ? 'healthy' : 'unhealthy'}`}
                                  >
                                    {analysisData.healthAssessment.isHealthy ? 'HEALTHY' : 'UNHEALTHY'}
                                  </span>
                                </div>
                                {analysisData.healthAssessment.confidence && (
                                  <span className="ai-doctor-health-confidence">
                                    {Math.round(analysisData.healthAssessment.confidence * 100)}% Confidence
                                  </span>
                                )}
                              </div>
                              <div className="ai-doctor-progress-container">
                                <div
                                  className={`ai-doctor-progress-bar ${analysisData.healthAssessment.isHealthy ? 'healthy' : 'unhealthy'}`}
                                  style={{
                                    width: `${Math.round(analysisData.healthAssessment.confidence * 100)}%`
                                  }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="ai-doctor-sections-container">
                          {/* Disease Diagnosis - Only show if plant is unhealthy */}
                          {analysisData.diagnosis && !analysisData.healthAssessment?.isHealthy && (
                            <div className="ai-doctor-collapsible-section ai-doctor-diagnosis-section">
                              <div className="ai-doctor-collapsible-header" onClick={toggleDiagnosis}>
                                <h3 className="ai-doctor-collapsible-title">
                                  <span className="ai-doctor-section-label">Diagnosis</span>
                                  <svg xmlns="http://www.w3.org/2000/svg" className="ai-doctor-section-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                </h3>
                                <svg xmlns="http://www.w3.org/2000/svg"
                                  className={`ai-doctor-collapsible-arrow ${diagnosisExpanded ? 'open' : ''}`}
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  width="20" height="20">
                                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className={`ai-doctor-collapsible-content ${diagnosisExpanded ? 'open' : ''}`}>
                                <div className="ai-doctor-diagnosis-title">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="ai-doctor-diagnosis-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="24" height="24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                  {analysisData.diagnosis.condition}
                                </div>
                                <p className="ai-doctor-diagnosis-description">
                                  {analysisData.diagnosis.description}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Treatment Recommendations - Only show if a plant is detected and has treatment recommendations */}
                          {analysisData.treatment && analysisData.treatment.recommendations && (
                            <div className="ai-doctor-collapsible-section ai-doctor-treatment-section">
                              <div className="ai-doctor-collapsible-header" onClick={toggleTreatment}>
                                <h3 className="ai-doctor-collapsible-title">
                                  <span className="ai-doctor-section-label">Treatment</span>
                                  <svg xmlns="http://www.w3.org/2000/svg" className="ai-doctor-section-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                  </svg>
                                </h3>
                                <svg xmlns="http://www.w3.org/2000/svg"
                                  className={`ai-doctor-collapsible-arrow ${treatmentExpanded ? 'open' : ''}`}
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  width="20" height="20">
                                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className={`ai-doctor-collapsible-content ${treatmentExpanded ? 'open' : ''}`}>
                                {renderTreatmentRecommendations(analysisData.treatment.recommendations)}
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      // No plant detected message - we don't show any health status section
                      <div className="ai-doctor-no-plant-message">
                        <svg xmlns="http://www.w3.org/2000/svg" className="ai-doctor-no-plant-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="48" height="48">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <h3 className="ai-doctor-no-plant-title">No Plant Detected</h3>
                        <p className="ai-doctor-no-plant-description">
                          We couldn't identify any plant in the uploaded image. Please upload a clear image of a plant for diagnosis.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="ai-doctor-empty-state">
                    {/* Empty state with interactive eyes */}
                    <InteractiveEyes isIdle={true} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Image Modal */}
        {showModal && (
          <ImageModal
            imageUrl={modalImage}
            onClose={closeImageModal}
          />
        )}
      </div>
    </div>
  );
};

export default AIDoctor;